/**
 * What each plan grants. Definitions in code, assignment in the database.
 *
 * ## Why this file exists rather than a `plans` table
 *
 * "Garden costs $12 and includes 200 credits" is a decision, not data. It should be reviewed like
 * code, ship with the build, be identical on every deployment, and be visible in a diff when it
 * changes. *Which account is on Garden* is the opposite: it changes constantly, it is written by
 * the admin console today and a billing webhook later, and it belongs in `users.plan`.
 *
 * Keeping the definitions here also means the browser can quote a plan without asking the server —
 * the pricing page, the account panel, and the upgrade prompt all read the same constants the
 * quota check reads, so the number a user is shown and the number they are held to cannot drift.
 *
 * ## Every field is a limit somebody has to enforce
 *
 * Nothing here is decorative. `monthlyCredits` is phase 3's allowance, `aiCallsPerMinute` is the
 * rate-limit bucket, `costCapUsd` is phase 4's unadvertised ceiling, and `projectLimit` and
 * `exportQuality` are phase 6's gates. `commercialUse` is the exception and is honest about it: it
 * is a licence term, not a check, because there is nothing in a landscape design that identifies
 * the use it will be put to.
 *
 * ## These numbers are a starting guess
 *
 * They come from `docs/PRICING_IMPLEMENTATION_PLAN.md`, which derives them from an estimated
 * per-credit cost that has not yet been reconciled against a provider invoice — see
 * `docs/PRICING_TODO.md` task 1.8. Pro's worst-case COGS exceeds its price on purpose: the cap
 * binds only for an account that burns every credit on the most expensive operation, and the
 * bring-your-own-key escape valve exists for exactly that account.
 */

export type PlanId = 'free' | 'garden' | 'pro';

/** Export resolution ceiling, ascending. Enforced in phase 6. */
export type ExportQuality = 'standard' | 'hd' | 'professional';

/**
 * Longest edge, in pixels, that a plan may download an image at.
 *
 * The ceiling is applied to the bytes leaving the app rather than to the capture, and that
 * separation is deliberate: a shot is captured at a size the enhance endpoint accepts, because
 * every shot has to stay directly enhanceable, and those sizes top out at 1536. Gating the capture
 * would therefore mean a Free account could not enhance at all, which is the opposite of what the
 * tier is for.
 *
 * **Garden and Pro currently produce identical files**, because nothing in the app yet renders
 * above 1536. That is a real gap in what Pro is sold as rather than a rounding error, and closing
 * it needs a full-resolution re-export — re-rendering a stored camera at 3840 — not a bigger
 * number here. See task 6.7 in `docs/PRICING_TODO.md`.
 */
export const EXPORT_LONG_EDGE: Record<ExportQuality, number> = {
  standard: 1024,
  hd: 1536,
  professional: 3840,
};

/**
 * The word for a ceiling, for a UI that has to explain why a download is smaller than the frame.
 *
 * Ascending order is load-bearing in one place — `EXPORT_QUALITY_ORDER` — and nowhere else.
 */
export const EXPORT_QUALITY_ORDER: readonly ExportQuality[] = ['standard', 'hd', 'professional'];

export const EXPORT_QUALITY_LABELS: Record<ExportQuality, string> = {
  standard: 'Standard',
  hd: 'HD',
  professional: 'Professional',
};

/**
 * The commercial licence each plan grants, in the words a user is shown.
 *
 * Stated because it is unenforceable, not in spite of it. There is nothing in a saved site that
 * says whether it is somebody's own garden or a job they are being paid for, so the only honest
 * mechanism is to say plainly what the plan permits and let the user hold themselves to it — which
 * requires the sentence to actually appear somewhere they will read it, rather than only in a
 * terms-of-service document nobody opens.
 *
 * These are the product's terms, not legal drafting. A real terms of service still has to be
 * written by somebody qualified to write one — see task 6.6 in `docs/PRICING_TODO.md`.
 */
export const COMMERCIAL_USE_TERMS: Record<CommercialUse, string> = {
  none: 'Personal use only. Designs made on this plan are not licensed for paid or client work.',
  personal:
    'Your own property, and unpaid help for friends and family. Paid client work needs Pro.',
  full: 'Client work included. Designs made on this plan may be used commercially and sold on.',
};

/**
 * Commercial licence granted by the plan.
 *
 * A term in the terms of service and a line in the account panel, deliberately not a code gate:
 * there is nothing in a saved site that says whether it is for someone's own garden or a client's,
 * so a check here could only ever be theatre.
 */
export type CommercialUse = 'none' | 'personal' | 'full';

export interface Plan {
  id: PlanId;
  /** The word on the badge and the heading on the pricing page. */
  name: string;
  /** US dollars per month at list price. Zero for Free, which is not a discount but the product. */
  priceUsdPerMonth: number;
  /**
   * Included credits per calendar month.
   *
   * One credit is roughly $0.04 of estimated provider cost — see `shared/ai/credits.ts`. Free's 20
   * is one complete site reconstruction: enough to reach the moment the product is for, exactly
   * once, which is the whole job of the free tier.
   *
   * A self-hosted deployment overrides *this plan's* figure with `AI_MONTHLY_CREDITS`, because
   * everyone on such a deployment is on Free and there is no billing to move them off it. Paid
   * plans always use the number stated here.
   */
  monthlyCredits: number;
  /**
   * AI calls per minute.
   *
   * A different control from the monthly allowance and aimed at a different failure: the allowance
   * is the ceiling on spend, this is what stops a runaway client reaching that ceiling inside a
   * minute. Free's 5 is above what a human ever generates by hand and well below what a loop does.
   */
  aiCallsPerMinute: number;
  /** Cloud projects. Null means unlimited. Local browser-only sites are never counted. */
  projectLimit: number | null;
  exportQuality: ExportQuality;
  /**
   * Unadvertised monthly ceiling on estimated provider cost, in dollars.
   *
   * The backstop behind the credit allowance rather than a restatement of it. The allowance can be
   * wrong — it rests on estimated prices that have not been reconciled — and this is the number
   * that bounds the damage when it is. Never shown to a user; phase 4 enforces it.
   */
  costCapUsd: number;
  commercialUse: CommercialUse;
}

export const PLANS: Record<PlanId, Plan> = {
  free: {
    id: 'free',
    name: 'Free',
    priceUsdPerMonth: 0,
    monthlyCredits: 20,
    aiCallsPerMinute: 5,
    projectLimit: 3,
    exportQuality: 'standard',
    costCapUsd: 2,
    commercialUse: 'none',
  },
  garden: {
    id: 'garden',
    name: 'Garden',
    priceUsdPerMonth: 12,
    monthlyCredits: 200,
    aiCallsPerMinute: 20,
    projectLimit: 25,
    exportQuality: 'hd',
    costCapUsd: 8,
    commercialUse: 'personal',
  },
  pro: {
    id: 'pro',
    name: 'Pro',
    priceUsdPerMonth: 29,
    monthlyCredits: 750,
    aiCallsPerMinute: 60,
    projectLimit: null,
    exportQuality: 'professional',
    costCapUsd: 20,
    commercialUse: 'full',
  },
};

/** What a row with no plan, or an unrecognised one, is treated as. */
export const DEFAULT_PLAN: PlanId = 'free';

/** Ascending, so a UI can render the ladder without hard-coding the order. */
export const PLAN_ORDER: readonly PlanId[] = ['free', 'garden', 'pro'];

export function isPlanId(value: unknown): value is PlanId {
  return value === 'free' || value === 'garden' || value === 'pro';
}

/**
 * Resolve a stored plan id to its definition, falling back to Free.
 *
 * The fallback is the point. `users.plan` is a plain text column, so a tier that is renamed or
 * retired in code leaves rows naming something this build has never heard of. Degrading those
 * accounts to Free is recoverable and obvious; throwing would take down every AI call and every
 * session response for the affected users, which is a far worse answer to a typo.
 */
export function planFor(id: string | null | undefined): Plan {
  return isPlanId(id) ? PLANS[id] : PLANS[DEFAULT_PLAN];
}
